import { c as _c } from "react/compiler-runtime";
import ThemedAvatar from 'shared/components/Themed/Avatar';
import Grid from '@precis-digital/kurama/Grid';
import { type SingleSelectDropdownItem } from '@precis-digital/kurama/SingleSelectDropdown';
import { styled, useTheme } from '@precis-digital/kurama/utils';
import { useQueryClientAccounts } from 'src/dataSource/api';
import { useShouldShowInternalFeatures } from 'shared/hooks/useShouldShowInternalFeatures';
import { uniq } from 'ramda';
import { useEffect, useMemo, type ReactElement, type ReactNode } from 'react';
import { type ClientAccountsResp } from 'shared/api/accounts';
import { type AnalyticsReqOrderBy, type Filter } from 'shared/api/analytics';
import type { RecommendationRuleConfigRequest, RecommendationRuleStatusRequest } from 'shared/api/recommendations';
import { useQueryString } from 'shared/components/Router';
import { StyledTypography1LineClamp } from 'shared/components/StyledTypography1LineClamp';
import { makeToastWithLoading } from 'shared/components/Toaster';
import { useCurrentClient, type Client } from 'shared/context/ClientContext';
import { addDays, format, subDays } from 'shared/dateFns';
import { FEATURE_FLAGS, useFeatureFlag } from 'shared/featureFlag';
import { OverScreen } from 'shared/overScreens/niceModalReact';
import { useTranslation } from 'shared/translations';
import { isNilOrEmpty, objectEntries, pluralToSingle } from 'shared/utils';
import { useQueryMembers } from 'src/workspace/api';
import { useMutationActivityFeed, useMutationCreateRecommendationRulesConfigs, useMutationCreateRecommendationsRulesStatuses, useMutationDeleteRecommendationRulesConfigs, useMutationDeleteRecommendationsRulesStatuses, useMutationUpdateRecommendationRulesConfigs, useMutationUpdateRecommendationsRulesStatuses, useQueryRecommendationsRulesConfigs, useQueryRecommendationsRulesMetadata, useQueryRecommendationsRulesStatuses } from './api';
import RecommendationCard from './components/RecommendationCard';
import { DATA_SOURCE_PLATFORM_TO_HC_PLATFORM_MAP, IGNORED_FOR_DAYS, RECOMMENDATIONS_POPUP_QUERY_PARAMETER_KEY, SORTING_SEVERITY_MAP, STATUS, TABS, UNASSIGNED } from './constants';
import type { GroupedRecommendationItem, GroupRecommendationByTypes, MutationFunction, RecommendationParamsValues, RecommendationRuleConfigRequestChanges, SeverityTypes, UpdateStatusOptions } from './types';
import { filterData, formatStatusAPIDate, getFeed, groupRecommendationItems, mapEventsData, mapLegacyLinkedinRuleId, mapStatusToAPI } from './utils';
interface Options {
  filters?: Filter[];
  orderBy?: AnalyticsReqOrderBy;
}
interface UseFeedProps {
  currentClient: Client;
  dataSources: {
    data: ClientAccountsResp[] | undefined;
    isPending: boolean;
    isSuccess: boolean;
  };
  options?: Options;
  groupBy?: GroupRecommendationByTypes;
}
interface UseFeedReturn {
  groupedData: GroupedRecommendationItem[];
  inactiveData: GroupedRecommendationItem[];
  isSuccess: boolean;
  isError: boolean;
  isPending: boolean;
  fetchFeed: MutationFunction;
  date: string;
  lastUpdatedTimestamp: string | null;
}
export const useFeed = ({
  currentClient,
  dataSources,
  groupBy = 'ruleIdAndDate',
  options
}: UseFeedProps): UseFeedReturn => {
  const theme = useTheme();
  const {
    value: hasAccessToTestingRule
  } = useFeatureFlag(FEATURE_FLAGS.recommendationsRules);
  const shouldShowInternalFeatures = useShouldShowInternalFeatures();
  const {
    mutate: fetchFeed,
    isPending: isMutationLoading,
    isSuccess: isMutationSuccess,
    isError: isMutationError,
    data: eventsData,
    status: mutationStatus
  } = useMutationActivityFeed(currentClient.id);
  const isMutationIdle = mutationStatus === 'idle';
  const shouldFetchRulesAndEvents = dataSources.isSuccess && dataSources.data != null && dataSources.data.length > 0;
  const {
    data: rulesData,
    isPending: isLoadingRules,
    isSuccess: isSuccessRules,
    isError: isErrorRules
  } = useQueryRecommendationsRulesMetadata({
    enabled: shouldFetchRulesAndEvents
  });
  const {
    data: recommendationsRulesConfigs,
    isPending: isLoadingRecommendationsRulesConfigs,
    isSuccess: isSuccessRecommendationsRulesConfigs,
    isError: isErrorRecommendationsRulesConfigs
  } = useQueryRecommendationsRulesConfigs({
    clientId: currentClient.id
  });
  const {
    data: recommendationsRulesStatuses,
    isPending: isLoadingRecommendationsRulesStatuses,
    isSuccess: isSuccessRecommendationsRulesStatuses,
    isError: isErrorRecommendationsRulesStatuses
  } = useQueryRecommendationsRulesStatuses({
    clientId: currentClient.id
  });
  const optionsKey = useMemo(() => {
    if (options?.filters == null) return 'no-filters';
    return JSON.stringify(options.filters);
  }, [options]);
  useEffect(() => {
    if (shouldFetchRulesAndEvents) {
      getFeed(fetchFeed, options)(currentClient);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adding options to dependency array puts the component in an infinite loop
  }, [fetchFeed, currentClient, dataSources.data, shouldFetchRulesAndEvents, optionsKey]);
  const mappedEventsData = useMemo(() => {
    if (eventsData != null && dataSources.data != null) {
      return mapEventsData(eventsData, dataSources.data).filter(({
        account
      }) => account != null);
    }
    return [];
  }, [eventsData, dataSources.data]);
  const {
    data,
    inactiveData,
    date,
    lastUpdatedTimestamp
  } = useMemo(() => {
    if (rulesData != null && eventsData != null && recommendationsRulesConfigs != null && recommendationsRulesStatuses != null) {
      return groupRecommendationItems({
        clientId: currentClient.id,
        events: mappedEventsData,
        recommendationsMetadata: rulesData,
        groupBy,
        recommendationsRulesStatuses,
        recommendationsRulesConfigs,
        hasAccessToTestingRule,
        shouldShowInternalFeatures,
        theme
      });
    }
    return {
      data: [],
      inactiveData: [],
      date: '',
      lastUpdatedTimestamp: null
    };
  }, [currentClient.id, rulesData, eventsData, recommendationsRulesConfigs, recommendationsRulesStatuses, mappedEventsData, groupBy, hasAccessToTestingRule, shouldShowInternalFeatures, theme]);
  const groupedDataWithEventsData: GroupedRecommendationItem[] = useMemo(() => {
    if (data.length > 0 && mappedEventsData.length > 0) {
      return data.map(group => {
        return {
          ...group,
          events: mappedEventsData.filter(event => event.ruleId === group.ruleId)
        };
      });
    }
    return [];
  }, [data, mappedEventsData]);
  const inactiveDataWithEventsData: GroupedRecommendationItem[] = useMemo(() => {
    return inactiveData.map(rule => {
      return {
        ...rule,
        events: []
      };
    });
  }, [inactiveData]);
  const isRulesAndConfigsReady = isSuccessRecommendationsRulesConfigs && isSuccessRecommendationsRulesStatuses;
  const isSuccess = shouldFetchRulesAndEvents ? isSuccessRules && isMutationSuccess && isRulesAndConfigsReady : isRulesAndConfigsReady;
  const isError = isErrorRules || isMutationError || isErrorRecommendationsRulesConfigs || isErrorRecommendationsRulesStatuses;
  const isLoading = isLoadingRecommendationsRulesConfigs || isLoadingRecommendationsRulesStatuses || shouldFetchRulesAndEvents && (isLoadingRules || isMutationLoading || isMutationIdle);
  return {
    groupedData: groupedDataWithEventsData,
    inactiveData: inactiveDataWithEventsData,
    date,
    lastUpdatedTimestamp,
    isSuccess,
    isError,
    isPending: isLoading,
    fetchFeed
  };
};
interface UseFeedHomeCardsReturn {
  isSuccess: boolean;
  isError: boolean;
  isPending: boolean;
  noDataSources: boolean;
  cards: ReactNode[] | null;
  date: string;
  allData: GroupedRecommendationItem[];
}
interface UseFeedHomeCardsProps {
  onRecommendationClick?: (recommendation: GroupedRecommendationItem) => void;
}
const sortByTimestampAndSeverityAndName = (a: GroupedRecommendationItem, b: GroupedRecommendationItem): number => {
  const aDate = new Date(a.timestamp).toISOString().split('T')[0];
  const bDate = new Date(b.timestamp).toISOString().split('T')[0];
  if (aDate < bDate) {
    return 1;
  }
  if (aDate > bDate) {
    return -1;
  }
  if (a.severity !== b.severity) {
    return SORTING_SEVERITY_MAP[b.severity] - SORTING_SEVERITY_MAP[a.severity];
  }
  return a.name.localeCompare(b.name);
};
export const useFeedHomeCards = t0 => {
  const $ = _c(33);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    onRecommendationClick
  } = t1;
  const {
    t
  } = useTranslation("recommendation");
  const today = new Date();
  const endOfToday = addDays(today, 1);
  const {
    currentClient,
    isLoading: isLoadingClient,
    isSuccess: isSuccessClient
  } = useCurrentClient();
  const {
    data: dataSources,
    isPending: isLoadingDataSources,
    isSuccess: isDataSourcesSuccess,
    isError: isErrorDataSources
  } = useQueryClientAccounts(currentClient.id);
  let t2;
  if ($[2] !== dataSources || $[3] !== isDataSourcesSuccess || $[4] !== isLoadingDataSources) {
    t2 = {
      data: dataSources,
      isPending: isLoadingDataSources,
      isSuccess: isDataSourcesSuccess
    };
    $[2] = dataSources;
    $[3] = isDataSourcesSuccess;
    $[4] = isLoadingDataSources;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = "timestamp";
  const t4 = "BETWEEN";
  const t5 = format(today, "yyyy-MM-dd");
  const t6 = format(endOfToday, "yyyy-MM-dd");
  let t7;
  if ($[6] !== t5 || $[7] !== t6) {
    t7 = {
      filters: [{
        field: t3,
        operator: t4,
        value: [t5, t6]
      }]
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== currentClient || $[10] !== t2 || $[11] !== t7) {
    t8 = {
      currentClient,
      dataSources: t2,
      options: t7
    };
    $[9] = currentClient;
    $[10] = t2;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const {
    groupedData,
    inactiveData,
    date,
    isPending: isLoadingFeed,
    isSuccess: isSuccessFeed,
    isError: isErrorFeed
  } = useFeed(t8);
  const noDataSources = isDataSourcesSuccess && dataSources?.length === 0;
  const isLoading = isLoadingClient || isLoadingDataSources || !noDataSources && isLoadingFeed;
  const isSuccess = noDataSources ? isSuccessClient && isDataSourcesSuccess : isSuccessFeed && isSuccessClient && isDataSourcesSuccess;
  const isError = isErrorFeed || isErrorDataSources;
  let t9;
  bb0: {
    if (noDataSources) {
      let t10;
      if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [];
        $[13] = t10;
      } else {
        t10 = $[13];
      }
      t9 = t10;
      break bb0;
    }
    if (!isSuccessFeed && !isErrorFeed) {
      t9 = null;
      break bb0;
    }
    if (groupedData.length === 0) {
      let t10;
      if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
        t10 = [];
        $[14] = t10;
      } else {
        t10 = $[14];
      }
      t9 = t10;
      break bb0;
    }
    let t10;
    if ($[15] !== groupedData || $[16] !== onRecommendationClick || $[17] !== t) {
      const sortedGroupedData = filterData(groupedData, {
        status: {
          unresolved: true,
          ongoing: true
        }
      }).sort(sortByTimestampAndSeverityAndName);
      let t11;
      if ($[19] !== onRecommendationClick || $[20] !== t) {
        t11 = rule => <RecommendationCard onClick={() => {
          onRecommendationClick?.(rule);
        }} data={rule} key={rule.messageId} t={t} />;
        $[19] = onRecommendationClick;
        $[20] = t;
        $[21] = t11;
      } else {
        t11 = $[21];
      }
      t10 = sortedGroupedData.map(t11);
      $[15] = groupedData;
      $[16] = onRecommendationClick;
      $[17] = t;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    t9 = t10;
  }
  const cards = t9;
  let t10;
  if ($[22] !== groupedData || $[23] !== inactiveData) {
    t10 = [...groupedData, ...inactiveData];
    $[22] = groupedData;
    $[23] = inactiveData;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const allData = t10;
  let t11;
  if ($[25] !== allData || $[26] !== cards || $[27] !== date || $[28] !== isError || $[29] !== isLoading || $[30] !== isSuccess || $[31] !== noDataSources) {
    t11 = {
      isPending: isLoading,
      isSuccess,
      isError,
      noDataSources,
      cards,
      date,
      allData
    };
    $[25] = allData;
    $[26] = cards;
    $[27] = date;
    $[28] = isError;
    $[29] = isLoading;
    $[30] = isSuccess;
    $[31] = noDataSources;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
};
type RecommendationRuleUpdateRequestParameters = {
  requestPayload: RecommendationRuleConfigRequest;
  configId: number;
  deleteConfig: boolean;
  updateConfig: boolean;
  createConfig: false;
} | {
  requestPayload: RecommendationRuleConfigRequest;
  configId: undefined;
  deleteConfig: false;
  updateConfig: false;
  createConfig: boolean;
};
interface GenerateRecommendationRuleConfigRequestProps {
  recommendationItem: GroupedRecommendationItem;
  changes: RecommendationRuleConfigRequestChanges;
}
export const generateRecommendationRuleConfigRequest = ({
  recommendationItem,
  changes
}: GenerateRecommendationRuleConfigRequestProps): RecommendationRuleUpdateRequestParameters => {
  const requestPayload: RecommendationRuleConfigRequest = {
    platform: DATA_SOURCE_PLATFORM_TO_HC_PLATFORM_MAP[recommendationItem.platform] ?? recommendationItem.platform,
    clientId: recommendationItem.clientId,
    ruleId: recommendationItem.ruleId
  };
  let hasChanges = false;
  let hasPayload = false;
  if (changes.name == null) {
    if (recommendationItem.name !== recommendationItem.originalName) {
      requestPayload.name = recommendationItem.name;
      hasPayload = true;
    }
  } else if (changes.name != null) {
    hasChanges ||= changes.name !== recommendationItem.name;
    if (changes.name !== recommendationItem.originalName) {
      requestPayload.name = changes.name;
      hasPayload = true;
    }
  }
  if (changes.recommendation == null) {
    if (recommendationItem.recommendation !== recommendationItem.originalRecommendation) {
      requestPayload.recommendation = recommendationItem.recommendation;
      hasPayload = true;
    }
  } else if (changes.recommendation != null) {
    hasChanges ||= changes.recommendation !== recommendationItem.recommendation;
    if (changes.recommendation !== recommendationItem.originalRecommendation) {
      requestPayload.recommendation = changes.recommendation;
      hasPayload = true;
    }
  }
  if (changes.severity == null) {
    if (recommendationItem.severity !== recommendationItem.originalSeverity) {
      requestPayload.severity = recommendationItem.severity;
      hasPayload = true;
    }
  } else if (changes.severity != null) {
    hasChanges ||= changes.severity !== recommendationItem.severity;
    if (changes.severity !== recommendationItem.originalSeverity) {
      requestPayload.severity = changes.severity;
      hasPayload = true;
    }
  }
  if (changes.paramsValues == null) {
    const paramsValuesEntries = Object.entries(recommendationItem.paramsValues).filter(([paramKey, paramValue]) => paramValue !== recommendationItem.params[paramKey].default);
    if (paramsValuesEntries.length !== 0) {
      requestPayload.params = Object.fromEntries(paramsValuesEntries);
      hasPayload = true;
    }
  } else if (changes.paramsValues != null) {
    const paramsValues: RecommendationParamsValues = {};
    objectEntries(recommendationItem.paramsValues).forEach(([paramKey, paramValue]) => {
      const defaultValue = recommendationItem.params[paramKey].default;
      const changeValue = changes.paramsValues?.[paramKey] ?? defaultValue;
      if (changeValue !== defaultValue) {
        paramsValues[paramKey] = changeValue;
        hasPayload = true;
      }
      hasChanges ||= changeValue !== paramValue;
    });
    if (hasPayload) {
      requestPayload.params = paramsValues;
    }
  }
  if (recommendationItem.configId != null) {
    return {
      requestPayload,
      configId: recommendationItem.configId,
      deleteConfig: hasChanges && !hasPayload,
      updateConfig: hasChanges && hasPayload,
      createConfig: false
    };
  } else {
    return {
      requestPayload,
      configId: undefined,
      deleteConfig: false,
      updateConfig: false,
      createConfig: hasChanges && hasPayload
    };
  }
};
type RecommendationRuleStatusUpdateRequestParameters = {
  requestPayload: RecommendationRuleStatusRequest;
  statusId: number;
  deleteStatus: boolean;
  updateStatus: boolean;
  createStatus: boolean;
} | {
  requestPayload: RecommendationRuleStatusRequest;
  statusId: number | undefined;
  deleteStatus: false;
  updateStatus: false;
  createStatus: boolean;
};
interface GenerateRecommendationRuleStatusRequestProps {
  recommendationItem: GroupedRecommendationItem;
  changes: UpdateStatusOptions;
}
export const generateRecommendationRuleStatusRequest = ({
  recommendationItem,
  changes
}: GenerateRecommendationRuleStatusRequestProps): RecommendationRuleStatusUpdateRequestParameters => {
  const requestPayload: RecommendationRuleStatusRequest = {
    clientId: recommendationItem.clientId,
    ruleId: recommendationItem.ruleId,
    ignored: recommendationItem.ignored,
    ongoing: recommendationItem.ongoing,
    ...(changes.status != null ? mapStatusToAPI(changes) : recommendationItem.ignoredUntil != null && {
      ignoredUntil: recommendationItem.ignoredUntil
    })
  };
  const newStatus = changes.status ?? recommendationItem.status;
  const newAssignee = changes.assignee === UNASSIGNED ? undefined : changes.assignee;
  let hasChanges = newStatus !== recommendationItem.status || changes.status === 'ignoredUntil' && changes.ignoredUntil !== recommendationItem.ignoredUntilDate;
  let hasPayload = newStatus !== 'unresolved';
  if (changes.assignee == null) {
    if (recommendationItem.assignee != null) {
      requestPayload.assignee = recommendationItem.assignee;
      hasPayload = true;
    }
  } else {
    hasChanges ||= newAssignee !== recommendationItem.assignee;
    if (newAssignee != null) {
      requestPayload.assignee = newAssignee;
      hasPayload = true;
    }
  }
  if (recommendationItem.statusId != null) {
    return {
      requestPayload,
      statusId: recommendationItem.statusId,
      deleteStatus: hasChanges && !hasPayload,
      updateStatus: hasChanges && hasPayload,
      createStatus: false
    };
  } else {
    return {
      requestPayload,
      statusId: undefined,
      deleteStatus: false,
      updateStatus: false,
      createStatus: hasChanges && hasPayload
    };
  }
};
interface UseUpdateRecommendationRuleReturn {
  updateRecommendation: (recommendationItem: GroupedRecommendationItem, changes: RecommendationRuleConfigRequestChanges) => Promise<void>;
  updateSeverity: (recommendationItem: GroupedRecommendationItem, newSeverity: SeverityTypes) => Promise<void>;
  updateStatus: (recommendationItem: GroupedRecommendationItem, options: UpdateStatusOptions) => Promise<void>;
  updateAssignee: (recommendationItem: GroupedRecommendationItem, newAssigneeId: number | typeof UNASSIGNED) => Promise<void>;
}
export const useUpdateRecommendationRule = () => {
  const $ = _c(26);
  const {
    currentClient
  } = useCurrentClient();
  const {
    data: members
  } = useQueryMembers(currentClient?.id);
  const {
    t
  } = useTranslation("recommendation");
  const {
    mutateAsync: createRecommendationRule
  } = useMutationCreateRecommendationRulesConfigs();
  const {
    mutateAsync: updateRecommendationRule
  } = useMutationUpdateRecommendationRulesConfigs();
  const {
    mutateAsync: deleteRecommendationRule
  } = useMutationDeleteRecommendationRulesConfigs();
  const {
    mutateAsync: createRecommendationsRuleStatus
  } = useMutationCreateRecommendationsRulesStatuses();
  const {
    mutateAsync: updateRecommendationsRuleStatus
  } = useMutationUpdateRecommendationsRulesStatuses();
  const {
    mutateAsync: deleteRecommendationsRuleStatus
  } = useMutationDeleteRecommendationsRulesStatuses();
  let t0;
  if ($[0] !== createRecommendationRule || $[1] !== deleteRecommendationRule || $[2] !== updateRecommendationRule) {
    t0 = async (recommendationItem, toastSuccess, toastError, t1) => {
      const {
        createConfig,
        deleteConfig,
        updateConfig,
        requestPayload,
        configId
      } = t1;
      if (!createConfig && !deleteConfig && !updateConfig) {
        return;
      }
      const {
        toastOnError,
        toastOnSuccess
      } = makeToastWithLoading();
      const onSuccess = () => {
        toastOnSuccess(toastSuccess);
      };
      const onError = () => {
        toastOnError(toastError);
      };
      if (createConfig) {
        await createRecommendationRule({
          data: requestPayload,
          onSuccess,
          onError
        });
      } else {
        if (updateConfig) {
          await updateRecommendationRule({
            data: requestPayload,
            configId,
            clientId: recommendationItem.clientId,
            onSuccess,
            onError
          });
        } else {
          if (deleteConfig) {
            await deleteRecommendationRule({
              configId,
              clientId: recommendationItem.clientId,
              onSuccess,
              onError
            });
          }
        }
      }
    };
    $[0] = createRecommendationRule;
    $[1] = deleteRecommendationRule;
    $[2] = updateRecommendationRule;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const executeUpdateRecommendation = t0;
  let t1;
  if ($[4] !== executeUpdateRecommendation || $[5] !== t) {
    t1 = async (recommendationItem_0, changes) => {
      await executeUpdateRecommendation(recommendationItem_0, t("toasts.updateRecommendationRule", {
        ruleName: recommendationItem_0.name
      }), t("toasts.updateRecommendationRuleFailed", {
        ruleName: recommendationItem_0.name
      }), generateRecommendationRuleConfigRequest({
        recommendationItem: recommendationItem_0,
        changes
      }));
    };
    $[4] = executeUpdateRecommendation;
    $[5] = t;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const updateRecommendation = t1;
  let t2;
  if ($[7] !== executeUpdateRecommendation || $[8] !== t) {
    t2 = async (recommendationItem_1, severity) => {
      await executeUpdateRecommendation(recommendationItem_1, t("toasts.updateSeverityRecommendationRule", {
        severity
      }), t("toasts.updateSeverityRecommendationRuleFailed", {
        severity
      }), generateRecommendationRuleConfigRequest({
        recommendationItem: recommendationItem_1,
        changes: {
          severity
        }
      }));
    };
    $[7] = executeUpdateRecommendation;
    $[8] = t;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const updateSeverity = t2;
  let t3;
  if ($[10] !== createRecommendationsRuleStatus || $[11] !== deleteRecommendationsRuleStatus || $[12] !== updateRecommendationsRuleStatus) {
    t3 = async (recommendationItem_2, toastSuccess_0, toastError_0, t4) => {
      const {
        createStatus,
        deleteStatus,
        updateStatus,
        requestPayload: requestPayload_0,
        statusId
      } = t4;
      if (!createStatus && !deleteStatus && !updateStatus) {
        return;
      }
      const {
        toastOnError: toastOnError_0,
        toastOnSuccess: toastOnSuccess_0
      } = makeToastWithLoading();
      const onSuccess_0 = () => {
        toastOnSuccess_0(toastSuccess_0);
      };
      const onError_0 = () => {
        toastOnError_0(toastError_0);
      };
      if (createStatus) {
        await createRecommendationsRuleStatus({
          data: requestPayload_0,
          onSuccess: onSuccess_0,
          onError: onError_0
        });
      } else {
        if (updateStatus) {
          await updateRecommendationsRuleStatus({
            data: requestPayload_0,
            statusId,
            clientId: recommendationItem_2.clientId,
            onSuccess: onSuccess_0,
            onError: onError_0
          });
        } else {
          if (deleteStatus) {
            await deleteRecommendationsRuleStatus({
              statusId,
              clientId: recommendationItem_2.clientId,
              onSuccess: onSuccess_0,
              onError: onError_0
            });
          }
        }
      }
    };
    $[10] = createRecommendationsRuleStatus;
    $[11] = deleteRecommendationsRuleStatus;
    $[12] = updateRecommendationsRuleStatus;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const executeUpdateRecommendationStatus = t3;
  let t4;
  if ($[14] !== executeUpdateRecommendationStatus || $[15] !== members || $[16] !== t) {
    t4 = async (recommendationItem_3, newAssigneeId) => {
      const newAssignee = members?.find(member => member.id === newAssigneeId);
      if (newAssigneeId === UNASSIGNED) {
        await executeUpdateRecommendationStatus(recommendationItem_3, t("toasts.unassignedRecommendationRule"), t("toasts.unassignedRecommendationRuleFailed"), generateRecommendationRuleStatusRequest({
          recommendationItem: recommendationItem_3,
          changes: {
            assignee: "UNASSIGNED"
          }
        }));
      } else {
        await executeUpdateRecommendationStatus(recommendationItem_3, t("toasts.assignedRecommendationRule", {
          assignee: newAssignee?.name ?? "..."
        }), t("toasts.assignedRecommendationRuleFailed", {
          assignee: newAssignee?.name ?? "..."
        }), generateRecommendationRuleStatusRequest({
          recommendationItem: recommendationItem_3,
          changes: {
            assignee: newAssigneeId
          }
        }));
      }
    };
    $[14] = executeUpdateRecommendationStatus;
    $[15] = members;
    $[16] = t;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const updateAssignee = t4;
  let t5;
  if ($[18] !== executeUpdateRecommendationStatus || $[19] !== t) {
    t5 = async (recommendationItem_4, options) => {
      await executeUpdateRecommendationStatus(recommendationItem_4, t("toasts.updateRecommendationRuleStatus", {
        status: options.status ?? "active"
      }), t("toasts.updateRecommendationRuleStatusFailed", {
        status: options.status ?? "active"
      }), generateRecommendationRuleStatusRequest({
        recommendationItem: recommendationItem_4,
        changes: options
      }));
    };
    $[18] = executeUpdateRecommendationStatus;
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const updateStatus_0 = t5;
  let t6;
  if ($[21] !== updateAssignee || $[22] !== updateRecommendation || $[23] !== updateSeverity || $[24] !== updateStatus_0) {
    t6 = {
      updateRecommendation,
      updateSeverity,
      updateStatus: updateStatus_0,
      updateAssignee
    };
    $[21] = updateAssignee;
    $[22] = updateRecommendation;
    $[23] = updateSeverity;
    $[24] = updateStatus_0;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  return t6;
};
export const useStatusOptions = t0 => {
  const $ = _c(4);
  const {
    ignoredUntil,
    ignoredUntilDate
  } = t0;
  const {
    t
  } = useTranslation("recommendation");
  let t1;
  if ($[0] !== ignoredUntil || $[1] !== ignoredUntilDate || $[2] !== t) {
    t1 = Object.values(STATUS).map(status => {
      const statusOption = {
        label: status.label,
        value: status.value,
        icon: status.icon
      };
      if (status.value === "ignored") {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        statusOption.subOptions = IGNORED_FOR_DAYS.map(days => ({
          label: t(`ignoredUntilOptions.ignoredFor${days}Days`),
          value: formatStatusAPIDate(addDays(today, days))
        }));
        if (ignoredUntil != null && !statusOption.subOptions.some(t2 => {
          const {
            value
          } = t2;
          return value === ignoredUntil;
        })) {
          statusOption.subOptions.unshift({
            label: t("ignoredUntilOptions.existingValue", {
              date: formatStatusAPIDate(subDays(ignoredUntilDate, 1))
            }),
            value: ignoredUntil
          });
        }
      }
      return statusOption;
    });
    $[0] = ignoredUntil;
    $[1] = ignoredUntilDate;
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const statusOptions = t1;
  return statusOptions;
};
interface UseEntitiesAffectedReturn {
  entities: string[] | null;
  entitiesLabel: string;
  entitiesTooltipTitle: string | null;
  entitiesTooltipBody: ReactElement | null;
}
export const useEntitiesAffected = (t0, t1) => {
  const $ = _c(24);
  const {
    defaultColumns,
    platformEntityType,
    outputColumns,
    events,
    totalNumEntitiesWithIssues,
    totalNumEntities
  } = t0;
  const useOutOf = t1 === undefined ? false : t1;
  const {
    t
  } = useTranslation("recommendation");
  let t2;
  if ($[0] !== platformEntityType) {
    t2 = () => platformEntityType.replace(/_/g, " ");
    $[0] = platformEntityType;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const getEntitiesLabel = t2;
  let t3;
  if ($[2] !== getEntitiesLabel || $[3] !== t || $[4] !== totalNumEntities || $[5] !== totalNumEntitiesWithIssues || $[6] !== useOutOf) {
    const getEntitiesAffectedDescriber = () => {
      const entitiesLabel = pluralToSingle(getEntitiesLabel(), totalNumEntitiesWithIssues);
      if (totalNumEntities === 0 || !useOutOf) {
        return entitiesLabel;
      }
      if (totalNumEntitiesWithIssues < totalNumEntities) {
        return `${entitiesLabel} (${t("affectedOutOf")} ${totalNumEntities})`;
      }
      return `${entitiesLabel} (${t("affectedAll")})`;
    };
    t3 = getEntitiesAffectedDescriber();
    $[2] = getEntitiesLabel;
    $[3] = t;
    $[4] = totalNumEntities;
    $[5] = totalNumEntitiesWithIssues;
    $[6] = useOutOf;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const entitiesLabel_0 = t3;
  const columnIdEntityName = defaultColumns[0];
  let t4;
  if ($[8] !== columnIdEntityName || $[9] !== events || $[10] !== outputColumns) {
    bb0: {
      if (columnIdEntityName == null) {
        let t5;
        if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
          t5 = {
            entities: null,
            entitiesTooltipTitle: null,
            entitiesTooltipBody: null
          };
          $[12] = t5;
        } else {
          t5 = $[12];
        }
        t4 = t5;
        break bb0;
      }
      let t5;
      if ($[13] !== columnIdEntityName) {
        t5 = t6 => {
          const {
            data
          } = t6;
          return data.map(item => String(item[columnIdEntityName]));
        };
        $[13] = columnIdEntityName;
        $[14] = t5;
      } else {
        t5 = $[14];
      }
      const entities = uniq(events.flatMap(t5));
      if (entities.length === 0) {
        t4 = {
          entities,
          entitiesTooltipTitle: null,
          entitiesTooltipBody: null
        };
        break bb0;
      }
      const entitiesTooltipTitle = outputColumns?.[columnIdEntityName]?.label ?? columnIdEntityName;
      const entitiesTooltipBody = <Grid display="flex" flexDirection="column" gap="4px">{entities.map(_temp)}</Grid>;
      let t6;
      if ($[15] !== entities || $[16] !== entitiesTooltipBody || $[17] !== entitiesTooltipTitle) {
        t6 = {
          entities,
          entitiesTooltipTitle,
          entitiesTooltipBody
        };
        $[15] = entities;
        $[16] = entitiesTooltipBody;
        $[17] = entitiesTooltipTitle;
        $[18] = t6;
      } else {
        t6 = $[18];
      }
      t4 = t6;
    }
    $[8] = columnIdEntityName;
    $[9] = events;
    $[10] = outputColumns;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const {
    entities: entities_0,
    entitiesTooltipBody: entitiesTooltipBody_0,
    entitiesTooltipTitle: entitiesTooltipTitle_0
  } = t4;
  let t5;
  if ($[19] !== entitiesLabel_0 || $[20] !== entitiesTooltipBody_0 || $[21] !== entitiesTooltipTitle_0 || $[22] !== entities_0) {
    t5 = {
      entities: entities_0,
      entitiesLabel: entitiesLabel_0,
      entitiesTooltipTitle: entitiesTooltipTitle_0,
      entitiesTooltipBody: entitiesTooltipBody_0
    };
    $[19] = entitiesLabel_0;
    $[20] = entitiesTooltipBody_0;
    $[21] = entitiesTooltipTitle_0;
    $[22] = entities_0;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
};
interface UseAccountsAffectedReturn {
  avatars: Array<{
    name: string;
    iconUrl: string;
  }> | null;
  avatarLabel: string;
  avatarsTooltipTitle: string | null;
  avatarsTooltipBody: ReactElement | null;
}
export const useAccountsAvatarsAffected: (recommendationItem: GroupedRecommendationItem) => UseAccountsAffectedReturn = t0 => {
  const $ = _c(29);
  const {
    accounts
  } = t0;
  const {
    t
  } = useTranslation("recommendation");
  let t1;
  if ($[0] !== accounts.length || $[1] !== t) {
    t1 = accounts.length === 1 ? t("account") : t("accounts");
    $[0] = accounts.length;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const avatarLabel = t1;
  let t2;
  bb0: {
    if (accounts.length === 0) {
      let t3;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t3 = {
          avatars: null,
          avatarsTooltipTitle: null,
          avatarsTooltipBody: null
        };
        $[3] = t3;
      } else {
        t3 = $[3];
      }
      t2 = t3;
      break bb0;
    }
    let T0;
    let avatars;
    let t3;
    let t4;
    let t5;
    let t6;
    if ($[4] !== accounts) {
      avatars = accounts.slice().sort(_temp2);
      T0 = Grid;
      t3 = "flex";
      t4 = "column";
      t5 = "4px";
      t6 = avatars.map(_temp3);
      $[4] = accounts;
      $[5] = T0;
      $[6] = avatars;
      $[7] = t3;
      $[8] = t4;
      $[9] = t5;
      $[10] = t6;
    } else {
      T0 = $[5];
      avatars = $[6];
      t3 = $[7];
      t4 = $[8];
      t5 = $[9];
      t6 = $[10];
    }
    let t7;
    if ($[11] !== T0 || $[12] !== t3 || $[13] !== t4 || $[14] !== t5 || $[15] !== t6) {
      t7 = <T0 display={t3} flexDirection={t4} gap={t5}>{t6}</T0>;
      $[11] = T0;
      $[12] = t3;
      $[13] = t4;
      $[14] = t5;
      $[15] = t6;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    const avatarsTooltipBody = t7;
    let t8;
    if ($[17] !== avatars.length || $[18] !== t) {
      t8 = avatars.length === 1 ? t("affectedAccount") : t("affectedAccounts");
      $[17] = avatars.length;
      $[18] = t;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    const avatarsTooltipTitle = t8;
    let t9;
    if ($[20] !== avatars || $[21] !== avatarsTooltipBody || $[22] !== avatarsTooltipTitle) {
      t9 = {
        avatars,
        avatarsTooltipTitle,
        avatarsTooltipBody
      };
      $[20] = avatars;
      $[21] = avatarsTooltipBody;
      $[22] = avatarsTooltipTitle;
      $[23] = t9;
    } else {
      t9 = $[23];
    }
    t2 = t9;
  }
  const {
    avatars: avatars_0,
    avatarsTooltipBody: avatarsTooltipBody_0,
    avatarsTooltipTitle: avatarsTooltipTitle_0
  } = t2;
  let t3;
  if ($[24] !== avatarLabel || $[25] !== avatarsTooltipBody_0 || $[26] !== avatarsTooltipTitle_0 || $[27] !== avatars_0) {
    t3 = {
      avatars: avatars_0,
      avatarLabel,
      avatarsTooltipTitle: avatarsTooltipTitle_0,
      avatarsTooltipBody: avatarsTooltipBody_0
    };
    $[24] = avatarLabel;
    $[25] = avatarsTooltipBody_0;
    $[26] = avatarsTooltipTitle_0;
    $[27] = avatars_0;
    $[28] = t3;
  } else {
    t3 = $[28];
  }
  return t3;
};
interface UseRecommendationPopupDetailsReturn {
  showPopup: (currentRecommendationItem?: GroupedRecommendationItem) => void;
  shouldShowPopup: () => boolean;
}
const showRecommendationPopup = (recommendationItem: GroupedRecommendationItem): void => {
  void OverScreen.show('recommendationsPopupV2', {
    data: recommendationItem,
    defaultTab: TABS.overview
  });
};
export const useRecommendationPopupDetails = t0 => {
  const $ = _c(16);
  const {
    groupedRecommendations
  } = t0;
  const {
    query
  } = useQueryString();
  let t1;
  if ($[0] !== query.ruleId) {
    t1 = typeof query.ruleId === "string" ? mapLegacyLinkedinRuleId(query.ruleId) : undefined;
    $[0] = query.ruleId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const requestedRuleId = t1;
  let t2;
  if ($[2] !== groupedRecommendations || $[3] !== requestedRuleId) {
    let t3;
    if ($[5] !== requestedRuleId) {
      t3 = item => item.ruleId === requestedRuleId;
      $[5] = requestedRuleId;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = groupedRecommendations.find(t3);
    $[2] = groupedRecommendations;
    $[3] = requestedRuleId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const recommendationItem = t2;
  let t3;
  if ($[7] !== recommendationItem) {
    t3 = currentRecommendationItem => {
      if (currentRecommendationItem != null) {
        showRecommendationPopup(currentRecommendationItem);
      } else {
        if (recommendationItem != null) {
          showRecommendationPopup(recommendationItem);
        }
      }
    };
    $[7] = recommendationItem;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== query.popup || $[10] !== recommendationItem?.ruleId || $[11] !== requestedRuleId) {
    t4 = () => query.popup === RECOMMENDATIONS_POPUP_QUERY_PARAMETER_KEY && !isNilOrEmpty(requestedRuleId) && recommendationItem?.ruleId === requestedRuleId;
    $[9] = query.popup;
    $[10] = recommendationItem?.ruleId;
    $[11] = requestedRuleId;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== t3 || $[14] !== t4) {
    t5 = {
      showPopup: t3,
      shouldShowPopup: t4
    };
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
};
const StyledTypographyAccountId = styled(StyledTypography1LineClamp)(() => ({
  marginTop: '-2px'
}));
function _temp(entity) {
  return <StyledTypography1LineClamp variant="body2" key={entity}>{entity}</StyledTypography1LineClamp>;
}
function _temp2(a, b) {
  return a.name.localeCompare(b.name);
}
function _temp3(account) {
  return <Grid key={account.id} display="flex" gap="8px" alignItems="center"><ThemedAvatar size="small" kind="image" imageUrl={account.iconUrl} /><Grid display="flex" flexDirection="column"><StyledTypography1LineClamp variant="body2">{account.name}</StyledTypography1LineClamp><StyledTypographyAccountId variant="body3">{account.id}</StyledTypographyAccountId></Grid></Grid>;
}